//src/context/CartContext.jsx
"use client";

/**
 * CartContext — Clean, reliable cart management.
 *
 * DESIGN PRINCIPLES:
 * ──────────────────
 * 1. SOURCE OF TRUTH
 *    - Guest (not logged in)        → localStorage only
 *    - Logged-in, no active session → localStorage (fallback, will migrate on first active session)
 *    - Logged-in, active session    → backend (HttpOnly-cookie auth, no accessToken needed in memory)
 *
 * 2. MIGRATION
 *    - When we get our first active session for a user, we check both backend AND localStorage.
 *    - If backend is empty but localStorage has items → push localStorage items to backend.
 *    - After successful migration, clear localStorage.
 *
 * 3. MUTATIONS (add / remove / update qty)
 *    - Active session → write to backend first, then sync local state from response.
 *    - No active session → write to localStorage optimistically.
 *
 * 4. STABILITY GUARD
 *    - `cartLoadedForUserId` ref prevents re-fetching on every pathname change or token refresh.
 *    - Only re-loads when the actual userId changes (login / logout).
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { getStorageItem, setStorageItem, removeStorageItem } from '@/utils/storage';
import * as cartService from '@/services/cartService';
import { getProductById } from '@/services/productService';
import { calculateCartTotal } from '@/services/pricingService';
import { getMinimumOrderQuantity } from '@/services/userTypeService';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

// ─── Constants ───────────────────────────────────────────────────────────────
const CART_STORAGE_KEY = 'cart_items';
const COUPON_STORAGE_KEY = 'applied_coupon';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const isValidObjectId = (v) => /^[a-fA-F0-9]{24}$/.test(String(v || ''));

const resolveProductId = (item) => {
  const id =
    item?.product?._id ||
    item?.product?.id ||
    item?.productId?._id ||
    item?.productId?.id ||
    item?.productId ||
    null;
  return id ? String(id) : null;
};

const resolveItemKey = (item) => {
  const k = item?.id || item?._id || resolveProductId(item);
  return k ? String(k) : null;
};

const normalizeStoredCart = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
};

const normalizeGuestEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const productId =
    entry.productId || entry?.product?._id || entry?.product?.id || null;
  if (!productId) return null;
  return {
    productId: String(productId),
    quantity: Math.max(1, Number(entry.quantity || 1) || 1),
    addedAt: entry.addedAt || null,
    snapshot: entry.snapshot || entry.product || null,
  };
};

const buildSnapshot = (product, fallbackId) => {
  if (!product || typeof product !== 'object') return fallbackId ? { _id: fallbackId } : null;
  const id = product._id || product.id || fallbackId || null;
  return {
    _id: id, id,
    slug: product.slug || null,
    name: product.name || null,
    images: Array.isArray(product.images) ? product.images.slice(0, 3) : [],
    productType: product.productType || null,
    manufacturerBrand: product.manufacturerBrand || null,
    vehicleBrand: product.vehicleBrand || null,
    stockQty: Number(product.stockQty || 0),
    retailPrice: product.retailPrice || null,
    wholesalePrice: product.wholesalePrice || null,
    minOrderQty: Number(product.minOrderQty || 1),
    minWholesaleQty: Number(product.minWholesaleQty || 1),
    ratingAvg: Number(product.ratingAvg || 0),
    ratingCount: Number(product.ratingCount || 0),
    vendor: product.vendor || null,
    categorySlug: product.categorySlug || null,
    categoryName: product.categoryName || null,
  };
};

const toStorageItems = (cartItems = []) =>
  cartItems
    .map((item) => {
      const productId = resolveProductId(item);
      return normalizeGuestEntry({
        productId,
        quantity: item?.quantity,
        addedAt: item?.addedAt || null,
        snapshot: buildSnapshot(item?.product, productId),
      });
    })
    .filter(Boolean);

const resolveMinQty = (product, user) =>
  Math.max(1, Number(getMinimumOrderQuantity(product, user) || 1));

// ─── Context ─────────────────────────────────────────────────────────────────
const CartContext = createContext(undefined);

const defaultCartContext = {
  items: [], addToCart: async () => false, removeFromCart: async () => {},
  updateQuantity: async () => {}, clearCart: async () => {},
  itemCount: 0, subtotal: 0, discount: 0, appliedCoupon: null,
  applyCoupon: async () => {}, removeCoupon: async () => {},
  loading: false, initializationLoading: false, cartSource: 'guest',
  hydrateFromBackend: async () => {},
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const CartProvider = ({ children }) => {
  const {
    isAuthenticated,   // Boolean(user) — true whenever user is in memory
    accessToken,       // may be null even when logged in (non-bootstrap routes)
    loading: authLoading,
    user,
    expireSession,
  } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initializationLoading, setInitializationLoading] = useState(true);
  const [cartSource, setCartSource] = useState('guest');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [backendDiscount, setBackendDiscount] = useState(0);

  // Tracks which userId we have already fully loaded the cart for.
  // Prevents re-fetching on every pathname change / token refresh.
  const cartLoadedForUserId = useRef(null);

  // ── syncWithBackend ──────────────────────────────────────────────────────
  const syncWithBackend = useCallback((backendCart) => {
    if (!backendCart) return;

    if (backendCart.items) {
      setItems((prevItems) => {
        // Build a map of rich product objects already in memory
        const richMap = new Map();
        prevItems.forEach((item) => {
          const pid = resolveProductId(item);
          if (pid && item.product) richMap.set(pid, item.product);
        });

        const usedRowIds = new Set();
        return backendCart.items.map((item, index) => {
          let product = item.product || item.productId || null;

          // Prefer the richer local product object if available
          if (typeof product === 'string') {
            product = richMap.get(product) || product;
          } else if (product && (product._id || product.id)) {
            const pid = String(product._id || product.id);
            const local = richMap.get(pid);
            if (local && Object.keys(local).length > Object.keys(product).length) {
              product = { ...local, ...product };
            }
          }

          const fallbackProductId = resolveProductId({ product, productId: item.productId }) || 'item';
          const rawId = item._id || item.id || null;
          const backendItemId = isValidObjectId(rawId) ? String(rawId) : null;
          const stableId = backendItemId || `${fallbackProductId}-${index}`;
          let rowId = String(stableId);
          if (usedRowIds.has(rowId)) rowId = `${rowId}-row-${index}`;
          usedRowIds.add(rowId);

          return {
            id: rowId,
            backendItemId,
            productId: fallbackProductId,
            product,
            quantity: item.quantity,
            unitPrice: Number(item.unitPrice ?? item.price ?? item.productPrice ?? 0) || 0,
            lineTotal: Number(item.lineTotal ?? item.total ?? item.subtotal ?? 0) || 0,
          };
        });
      });
    }

    setBackendDiscount(backendCart.discountAmount || 0);
    if (backendCart.couponCode) {
      setAppliedCoupon({ code: backendCart.couponCode, isBackend: true });
    } else {
      setAppliedCoupon(null);
    }
  }, []);

  // ── loadGuestCart ────────────────────────────────────────────────────────
  const loadGuestCart = useCallback(async () => {
    try {
      const stored = getStorageItem(CART_STORAGE_KEY);
      const parsed = normalizeStoredCart(stored).map(normalizeGuestEntry).filter(Boolean);

      if (!parsed.length) {
        setItems([]);
        return;
      }

      const products = await Promise.all(
        parsed.map((e) => getProductById(e.productId, { silent: true }).catch(() => null))
      );

      const hydrated = parsed
        .map((entry, i) => {
          const product = products[i] || entry.snapshot;
          if (!product) return null;
          return {
            id: `${entry.productId}-guest`,
            product,
            quantity: entry.quantity,
            addedAt: entry.addedAt || null,
          };
        })
        .filter(Boolean);

      setItems(hydrated);
      setStorageItem(CART_STORAGE_KEY, parsed); // keep normalized

      const storedCoupon = getStorageItem(COUPON_STORAGE_KEY);
      if (storedCoupon) {
        try {
          setAppliedCoupon(typeof storedCoupon === 'string' ? JSON.parse(storedCoupon) : storedCoupon);
        } catch { /* ignore */ }
      }
    } catch (err) {
      logger.error('[CART] Failed to load guest cart:', err);
      setItems([]);
    }
  }, []);

  // ── persistLocalCart ─────────────────────────────────────────────────────
  // Always save to localStorage for guest mode. In backend mode we DON'T
  // touch localStorage (backend is source of truth).
  const persistLocalCart = useCallback((cartItems) => {
    if (cartSource !== 'guest') return;
    try {
      setStorageItem(CART_STORAGE_KEY, toStorageItems(cartItems));
    } catch (err) {
      logger.error('[CART] Failed to persist local cart:', err);
    }
  }, [cartSource]);

  // ── migrateLocalToBackend ────────────────────────────────────────────────
  // Pushes any localStorage items to the backend cart and clears localStorage.
  // Called when we first get an active session and backend cart is empty.
  const migrateLocalToBackend = useCallback(async () => {
    const stored = getStorageItem(CART_STORAGE_KEY);
    const guestItems = normalizeStoredCart(stored).map(normalizeGuestEntry).filter(Boolean);
    if (!guestItems.length) return false;

    logger.warn('[CART] Migrating local cart to backend:', guestItems.length, 'items');
    try {
      await cartService.replaceCart(accessToken, guestItems);
      removeStorageItem(CART_STORAGE_KEY);
      return true;
    } catch (err) {
      logger.error('[CART] Migration failed:', err);
      return false;
    }
  }, [accessToken]);

  // ── Main cart loading effect ─────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const safetyTimeout = setTimeout(() => {
      if (isMounted && initializationLoading) {
        logger.warn('[CART] Load timed out — forcing completion');
        setInitializationLoading(false);
        setLoading(false);
      }
    }, 5000);

    const loadCart = async () => {
      if (authLoading) return; // wait for auth to stabilize

      const currentUserId = user?.id || user?._id || null;

      if (isAuthenticated) {
        // ── CASE A: Logged in ──────────
        // Only load once per userId to prevent re-wiping on navigation.
        if (cartLoadedForUserId.current === currentUserId && currentUserId) {
          if (isMounted) { setInitializationLoading(false); setLoading(false); }
          return;
        }

        if (isMounted) {
          // Only show loading spinner if this is a fresh load (no items in memory yet).
          // If items already exist, re-sync silently in background to prevent flash.
          setCartSource('backend');
          setItems((current) => { if (current.length === 0) setLoading(true); return current; });
        }

        try {
          const backendCart = await Promise.race([
            cartService.getCart(accessToken, { throwOnAuthError: true }),
            new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 8000)),
          ]);

          if (!isMounted) return;

          const backendItems = Array.isArray(backendCart?.items) ? backendCart.items : [];

          if (backendCart && backendItems.length > 0) {
            // ── CASE A1: Backend has items → use them ────────────────────
            syncWithBackend(backendCart);
            cartLoadedForUserId.current = currentUserId;

          } else if (backendCart) {
            // ── CASE A2: Backend is empty → check localStorage ───────────
            const migrated = await migrateLocalToBackend();
            if (migrated) {
              // Re-fetch after migration
              const freshCart = await cartService.getCart(accessToken);
              if (isMounted && freshCart && Array.isArray(freshCart.items) && freshCart.items.length > 0) {
                syncWithBackend(freshCart);
              } else if (isMounted) {
                setItems([]); setBackendDiscount(0);
              }
            } else {
              // Nothing in localStorage either — truly empty cart
              setItems([]); setBackendDiscount(0);
            }
            cartLoadedForUserId.current = currentUserId;

          } else {
            // ── CASE A3: Backend call returned null (network error) ───────
            // Preserve whatever is in memory; do NOT wipe.
            logger.warn('[CART] Backend unavailable — preserving current state');
            // Still mark as loaded so we don't loop
            cartLoadedForUserId.current = currentUserId;
          }

        } catch (err) {
          if (err?.status === 401) {
            // Remembered user, but the session is gone (the API client already tried a refresh).
            // Forget the user; this effect re-runs as a guest and loads the guest cart.
            logger.warn('[CART] Session expired — continuing as guest');
            expireSession();
            return;
          }
          logger.error('[CART] Failed to load backend cart:', err);
          // On error, keep existing items (don't wipe) and mark backend mode
          if (isMounted) setCartSource('backend');
          cartLoadedForUserId.current = currentUserId;
        }

      } else {
        // ── CASE C: Not logged in → pure guest cart ───────────────────────
        cartLoadedForUserId.current = null;
        if (isMounted) {
          setCartSource('guest');
          await loadGuestCart();
        }
      }
    };

    loadCart().finally(() => {
      if (isMounted) { setLoading(false); setInitializationLoading(false); }
    });

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, user?._id, authLoading]);

  // ── hydrateFromBackend (called by AuthContext after login migration) ─────
  const hydrateFromBackend = useCallback(async () => {
    if (!isAuthenticated) return;
    const currentUserId = user?.id || user?._id || null;
    cartLoadedForUserId.current = null; // force re-fetch
    setLoading(true);
    setCartSource('backend');
    try {
      const backendCart = await cartService.getCart(accessToken);
      if (backendCart) {
        syncWithBackend(backendCart);
        cartLoadedForUserId.current = currentUserId;
      }
    } catch (err) {
      logger.error('[CART] Failed to hydrate cart:', err);
      toast.error('Failed to load your cart');
    } finally {
      setLoading(false);
    }
  }, [accessToken, isAuthenticated, user, syncWithBackend]);

  // ── addToCart ────────────────────────────────────────────────────────────
  const addToCart = useCallback(async (product, quantity = 1) => {
    if (authLoading) { toast.info('Please wait, restoring your session...'); return false; }

    const productId = product?._id || product?.id;
    if (!productId) { logger.error('[CART] Missing product id'); return false; }

    const safeQty = Math.max(1, Number(quantity) || 1);
    const minQty = resolveMinQty(product, user);
    const normalizedQty = Math.max(minQty, safeQty);
    if (normalizedQty !== safeQty) toast.info(`Minimum order quantity is ${minQty}. Quantity adjusted.`);

    const isRealId = isValidObjectId(productId);

    if (cartSource === 'backend' && isAuthenticated) {
      if (!isRealId) {
        toast.error('This item cannot be added right now. Please refresh and try again.');
        return false;
      }
      try {
        await cartService.addToCart(accessToken, { productId, quantity: normalizedQty });
        const freshCart = await cartService.getCart(accessToken);
        if (!freshCart) { toast.error('Cart sync failed. Please refresh.'); return false; }

        // Verify the item actually landed in the backend cart
        const landed = Array.isArray(freshCart.items) && freshCart.items.some((item) => {
          const id =
            item?.product?._id || item?.product?.id ||
            item?.productId?._id || item?.productId?.id ||
            item?.productId || null;
          return id ? String(id) === String(productId) : false;
        });

        if (!landed) { toast.error('Item was not added. Please try again.'); return false; }
        syncWithBackend(freshCart);
        return true;
      } catch (err) {
        logger.error('[CART] Failed to add to backend cart:', err);
        toast.error(err?.message || 'Failed to add item to cart');
        return false;
      }
    }

    // Guest / non-active-session flow → optimistic localStorage add
    setItems((prev) => {
      const targetId = String(productId);
      const existingIndex = prev.findIndex((item) => resolveProductId(item) === targetId);
      let updated;
      if (existingIndex >= 0) {
        updated = prev.map((item, i) =>
          i === existingIndex ? { ...item, quantity: item.quantity + normalizedQty } : item
        );
      } else {
        updated = [...prev, {
          id: `${productId}-${Date.now()}`,
          backendItemId: null,
          product,
          quantity: normalizedQty,
        }];
      }
      // persist immediately inside the state setter (synchronous)
      try { setStorageItem(CART_STORAGE_KEY, toStorageItems(updated)); } catch { /* ignore */ }
      return updated;
    });
    return true;
  }, [cartSource, accessToken, isAuthenticated, user, authLoading, syncWithBackend]);

  // ── removeFromCart ───────────────────────────────────────────────────────
  const removeFromCart = useCallback(async (itemId) => {
    if (!itemId && itemId !== 0) { logger.warn('[CART] removeFromCart: missing itemId'); return; }
    const targetId = String(itemId);

    if (cartSource === 'backend' && isAuthenticated) {
      // Read the backendItemId BEFORE touching state
      const item = items.find((i) => resolveItemKey(i) === targetId);
      const candidate = item?.backendItemId || item?._id || (isValidObjectId(item?.id) ? item.id : null);
      const backendItemId = isValidObjectId(candidate) ? String(candidate) : null;

      if (!backendItemId) {
        logger.warn('[CART] removeFromCart: no valid backendItemId for', targetId);
        return;
      }

      // Optimistic remove
      setItems((prev) => prev.filter((i) => resolveItemKey(i) !== targetId));

      try {
        await cartService.removeFromCart(accessToken, backendItemId);
        const freshCart = await cartService.getCart(accessToken);
        if (freshCart) syncWithBackend(freshCart);
      } catch (err) {
        logger.error('[CART] Failed to remove from backend:', err);
        toast.error('Failed to remove item. Refreshing cart...');
        // Revert: re-fetch to restore correct state
        const freshCart = await cartService.getCart(accessToken).catch(() => null);
        if (freshCart) syncWithBackend(freshCart);
      }
      return;
    }

    // Guest mode
    setItems((prev) => {
      const updated = prev.filter((i) => resolveItemKey(i) !== targetId);
      try { setStorageItem(CART_STORAGE_KEY, toStorageItems(updated)); } catch { /* ignore */ }
      return updated;
    });
  }, [cartSource, accessToken, isAuthenticated, items, syncWithBackend]);

  // ── updateQuantity ───────────────────────────────────────────────────────
  const updateQuantity = useCallback(async (itemId, quantity) => {
    if (quantity < 1 || !itemId) return;
    const targetId = String(itemId);

    if (cartSource === 'backend' && isAuthenticated) {
      // Read backendItemId BEFORE touching state
      const item = items.find((i) => resolveItemKey(i) === targetId);
      const candidate = item?.backendItemId || item?._id || (isValidObjectId(item?.id) ? item.id : null);
      const backendItemId = isValidObjectId(candidate) ? String(candidate) : null;
      const minQty = resolveMinQty(item?.product, user);
      const enforcedQty = Math.max(minQty, quantity);

      if (!backendItemId) {
        logger.warn('[CART] updateQuantity: no valid backendItemId for', targetId);
        return;
      }

      // Optimistic update
      setItems((prev) =>
        prev.map((i) => resolveItemKey(i) === targetId ? { ...i, quantity: enforcedQty } : i)
      );

      try {
        await cartService.updateCartItem(accessToken, backendItemId, enforcedQty);
        const freshCart = await cartService.getCart(accessToken);
        if (freshCart) syncWithBackend(freshCart);
      } catch (err) {
        logger.error('[CART] Failed to update quantity in backend:', err);
        toast.error('Failed to update quantity. Refreshing cart...');
        const freshCart = await cartService.getCart(accessToken).catch(() => null);
        if (freshCart) syncWithBackend(freshCart);
      }
      return;
    }

    // Guest mode
    setItems((prev) => {
      const updated = prev.map((i) => {
        if (resolveItemKey(i) !== targetId) return i;
        const minQty = resolveMinQty(i.product, user);
        return { ...i, quantity: Math.max(minQty, quantity) };
      });
      try { setStorageItem(CART_STORAGE_KEY, toStorageItems(updated)); } catch { /* ignore */ }
      return updated;
    });
  }, [cartSource, accessToken, isAuthenticated, items, user, syncWithBackend]);

  // ── clearCart ────────────────────────────────────────────────────────────
  const clearCart = useCallback(async () => {
    setItems([]);
    setAppliedCoupon(null);
    setBackendDiscount(0);
    removeStorageItem(CART_STORAGE_KEY);
    removeStorageItem(COUPON_STORAGE_KEY);

    if (cartSource === 'backend' && isAuthenticated) {
      try {
        const promises = items
          .map((item) => {
            const candidate = item?.backendItemId || item?._id || (isValidObjectId(item?.id) ? item.id : null);
            const id = isValidObjectId(candidate) ? String(candidate) : null;
            return id ? cartService.removeFromCart(accessToken, id) : null;
          })
          .filter(Boolean);
        await Promise.all(promises);
      } catch (err) {
        logger.error('[CART] Failed to clear backend cart:', err);
        toast.error('Failed to sync cart changes.');
      }
    }
  }, [cartSource, accessToken, isAuthenticated, items]);

  // ── applyCoupon ──────────────────────────────────────────────────────────
  const applyCoupon = useCallback(async (coupon) => {
    if (cartSource === 'backend' && isAuthenticated) {
      try {
        const updatedCart = await cartService.applyCoupon(accessToken, coupon.code);
        syncWithBackend(updatedCart);
        toast.success(`Coupon ${coupon.code} applied!`);
      } catch (err) {
        logger.error('[CART] Failed to apply coupon:', err);
        toast.error(err.message || 'Failed to apply coupon');
      }
    } else {
      setAppliedCoupon(coupon);
      setStorageItem(COUPON_STORAGE_KEY, coupon);
      toast.success(`Coupon ${coupon.code} applied!`);
    }
  }, [cartSource, accessToken, isAuthenticated, syncWithBackend]);

  // ── removeCoupon ─────────────────────────────────────────────────────────
  const removeCoupon = useCallback(async () => {
    if (cartSource === 'backend' && isAuthenticated) {
      try {
        const updatedCart = await cartService.removeCoupon(accessToken);
        syncWithBackend(updatedCart);
        toast.success('Coupon removed');
      } catch (err) {
        logger.error('[CART] Failed to remove coupon:', err);
        toast.error('Failed to remove coupon');
      }
    } else {
      setAppliedCoupon(null);
      removeStorageItem(COUPON_STORAGE_KEY);
      toast.success('Coupon removed');
    }
  }, [cartSource, accessToken, isAuthenticated, syncWithBackend]);

  // ── Computed ─────────────────────────────────────────────────────────────
  const itemCount = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const subtotal = calculateCartTotal(items, user);
  const discount =
    cartSource === 'backend'
      ? backendDiscount
      : appliedCoupon
      ? appliedCoupon.type === 'percent'
        ? Math.round(subtotal * appliedCoupon.discount)
        : Math.min(appliedCoupon.discount, subtotal)
      : 0;

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        itemCount,
        subtotal,
        discount,
        appliedCoupon,
        applyCoupon,
        removeCoupon,
        loading,
        initializationLoading,
        cartSource,
        hydrateFromBackend,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  return context || defaultCartContext;
};